import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createChartAccount,
  createDetailNode,
  deleteChartAccount,
  getChartAccountLevels,
  getChartAccounts,
  getDetailNodes,
  updateChartAccount,
  updateChartAccountLevels,
} from '../services/treasuryAPI';

export const DETAIL_KEYS = [
  'detail_level_1',
  'detail_level_2',
  'detail_level_3',
  'detail_level_4',
  'detail_level_5',
  'detail_level_6',
];

export const EMPTY_CHART_ACCOUNT = {
  detail_level_1_id: '',
  detail_level_2_id: '',
  detail_level_3_id: '',
  detail_level_4_id: '',
  detail_level_5_id: '',
  detail_level_6_id: '',
  notes: '',
};

export const normalize = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

export default function useFinanceChartConfig() {
  const [chartAccounts, setChartAccounts] = useState([]);
  const [chartLevels, setChartLevels] = useState([]);
  const [detailNodes, setDetailNodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPath, setSelectedPath] = useState([]);
  const [treeSearch, setTreeSearch] = useState('');

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const [levelsPayload, chartAccountsPayload, detailNodesPayload] = await Promise.all([
        getChartAccountLevels(),
        getChartAccounts(),
        getDetailNodes(),
      ]);
      setChartLevels(Array.isArray(levelsPayload) ? levelsPayload : []);
      setChartAccounts(Array.isArray(chartAccountsPayload) ? chartAccountsPayload : []);
      setDetailNodes(Array.isArray(detailNodesPayload) ? detailNodesPayload : []);
    } catch (err) {
      setError(err.message || 'Error cargando configuración financiera.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const detailLevelsOrdered = useMemo(() => {
    const byKey = new Map((chartLevels || []).map((row) => [row.level_key, row]));
    return DETAIL_KEYS.map((levelKey, idx) => {
      const row = byKey.get(levelKey);
      return {
        level_key: levelKey,
        level_order: row ? Number(row.level_order || idx + 1) : idx + 1,
        level_name: row?.level_name || `Detalle Nivel ${idx + 1}`,
        is_enabled: row ? Number(row.is_enabled) : 0,
      };
    });
  }, [chartLevels]);

  const hierarchyLevels = useMemo(() => {
    const enabled = detailLevelsOrdered.filter((row) => Number(row.is_enabled) === 1);
    return enabled.length ? enabled : detailLevelsOrdered;
  }, [detailLevelsOrdered]);

  const detailNodesByLevel = useMemo(() => {
    const map = new Map();
    hierarchyLevels.forEach((level) => {
      map.set(
        level.level_key,
        detailNodes
          .filter((node) => node.level_key === level.level_key)
          .sort((a, b) => {
            const codeA = String(a.full_code || '');
            const codeB = String(b.full_code || '');
            if (codeA && codeB) {
              const byCode = codeA.localeCompare(codeB);
              if (byCode !== 0) return byCode;
            }
            if (codeA && !codeB) return -1;
            if (!codeA && codeB) return 1;
            return String(a.node_name || '').localeCompare(String(b.node_name || ''));
          })
      );
    });
    return map;
  }, [detailNodes, hierarchyLevels]);

  const detailNodeById = useMemo(() => {
    const map = new Map();
    detailNodes.forEach((node) => map.set(Number(node.id), node));
    return map;
  }, [detailNodes]);

  const countRowsForPath = useCallback(
    (path) =>
      chartAccounts.filter((row) =>
        path.every((segment) => {
          const rowValue = String(row[segment.levelKey] || '').trim() || '(Sin valor)';
          return normalize(rowValue) === normalize(segment.value);
        })
      ).length,
    [chartAccounts]
  );

  const buildHierarchyTree = useCallback(
    (levels, depth = 0, parentNodeId = null, parentPath = []) => {
      if (depth >= levels.length) return [];
      const level = levels[depth];
      const currentNodes = (detailNodesByLevel.get(level.level_key) || []).filter(
        (node) =>
          (node.parent_node_id === null && parentNodeId === null) ||
          Number(node.parent_node_id) === Number(parentNodeId)
      );
      return currentNodes.map((node) => {
        const nextPath = [
          ...parentPath,
          {
            levelKey: level.level_key,
            value: node.node_name,
            label: node.node_name,
            nodeId: Number(node.id),
          },
        ];
        return {
          level,
          label: node.node_name,
          value: node.node_name,
          nodeId: Number(node.id),
          path: nextPath,
          count: countRowsForPath(nextPath),
          children: buildHierarchyTree(levels, depth + 1, Number(node.id), nextPath),
        };
      });
    },
    [countRowsForPath, detailNodesByLevel]
  );

  const hierarchyTree = useMemo(
    () => buildHierarchyTree(hierarchyLevels),
    [buildHierarchyTree, hierarchyLevels]
  );

  useEffect(() => {
    if (!hierarchyTree.length) {
      setSelectedPath([]);
      return;
    }
    const pathExists = (nodes, path, depth = 0) => {
      if (!path.length) return true;
      const target = path[depth];
      const match = nodes.find(
        (node) =>
          node.level.level_key === target.levelKey &&
          normalize(node.value) === normalize(target.value)
      );
      if (!match) return false;
      if (depth === path.length - 1) return true;
      return pathExists(match.children || [], path, depth + 1);
    };
    if (!pathExists(hierarchyTree, selectedPath)) {
      setSelectedPath(hierarchyTree[0].path);
    }
  }, [hierarchyTree, selectedPath]);

  const filterTree = useCallback((nodes, query) => {
    if (!query) return nodes;
    const q = query.trim().toLowerCase();
    const walk = (list) =>
      list
        .map((node) => {
          const children = walk(node.children || []);
          const code = detailNodeById.get(Number(node.nodeId))?.full_code || '';
          const hay = `${code} ${node.label}`.toLowerCase();
          if (hay.includes(q) || children.length) {
            return { ...node, children };
          }
          return null;
        })
        .filter(Boolean);
    return walk(nodes);
  }, [detailNodeById]);

  const visibleTree = useMemo(
    () => filterTree(hierarchyTree, treeSearch),
    [filterTree, hierarchyTree, treeSearch]
  );

  const filteredChartAccounts = useMemo(() => {
    if (!selectedPath.length) return chartAccounts;
    return chartAccounts.filter((row) =>
      selectedPath.every((segment) => {
        const rowValue = String(row[segment.levelKey] || '').trim() || '(Sin valor)';
        return normalize(rowValue) === normalize(segment.value);
      })
    );
  }, [chartAccounts, selectedPath]);

  const selectedPathLabel = useMemo(() => {
    if (!selectedPath.length) return 'Todos los registros';
    return selectedPath
      .map((segment) => {
        const level = detailLevelsOrdered.find((row) => row.level_key === segment.levelKey);
        const levelName = level?.level_name || segment.levelKey;
        const node = segment.nodeId ? detailNodeById.get(Number(segment.nodeId)) : null;
        const codePrefix = node?.full_code ? `${node.full_code} ` : '';
        return `${levelName}: ${codePrefix}${segment.label}`;
      })
      .join(' / ');
  }, [selectedPath, detailLevelsOrdered, detailNodeById]);

  const findNodeIdByPath = useCallback(
    (row) => {
      let parentNodeId = null;
      const idMap = {};
      for (let idx = 0; idx < hierarchyLevels.length; idx += 1) {
        const level = hierarchyLevels[idx];
        const rawValue = String(row[level.level_key] || '').trim();
        if (!rawValue) {
          idMap[`${level.level_key}_id`] = '';
          continue;
        }
        const levelNodes = detailNodesByLevel.get(level.level_key) || [];
        const match = levelNodes.find(
          (node) =>
            normalize(node.node_name) === normalize(rawValue) &&
            ((node.parent_node_id === null && parentNodeId === null) ||
              Number(node.parent_node_id) === Number(parentNodeId))
        );
        if (!match) {
          idMap[`${level.level_key}_id`] = '';
          parentNodeId = null;
          continue;
        }
        idMap[`${level.level_key}_id`] = String(match.id);
        parentNodeId = Number(match.id);
      }
      return idMap;
    },
    [detailNodesByLevel, hierarchyLevels]
  );

  const updateLevelDraft = (levelKey, field, value) => {
    setChartLevels((prev) =>
      prev.map((row) =>
        row.level_key === levelKey
          ? {
              ...row,
              [field]: value,
            }
          : row
      )
    );
  };

  const saveLevels = async () => {
    await updateChartAccountLevels(
      detailLevelsOrdered.map((level) => ({
        level_key: level.level_key,
        level_name: String(level.level_name || '').trim() || level.level_key,
        is_enabled: String(level.is_enabled) === '1' || level.is_enabled === true,
      }))
    );
    await load();
  };

  const saveDetailNode = async ({ level_key, parent_node_id, node_name }) => {
    const currentIndex = hierarchyLevels.findIndex((row) => row.level_key === level_key);
    await createDetailNode({
      level_key,
      node_name: String(node_name || '').trim(),
      parent_node_id: currentIndex > 0 ? Number(parent_node_id) : null,
    });
    await load();
  };

  const saveChartAccount = async (formData, editingId = null) => {
    const payload = {
      category: '',
      account: '',
      subaccount: '',
      notes: formData.notes || '',
    };
    DETAIL_KEYS.forEach((levelKey) => {
      const selectedId = Number(formData[`${levelKey}_id`] || 0);
      const node = selectedId ? detailNodeById.get(selectedId) : null;
      payload[levelKey] = node?.node_name || '';
    });
    if (editingId) {
      await updateChartAccount(editingId, payload);
    } else {
      await createChartAccount(payload);
    }
    await load();
  };

  const removeChartAccounts = async (rows) => {
    const list = Array.isArray(rows) ? rows : [rows];
    await Promise.all(list.filter((row) => row?.id).map((row) => deleteChartAccount(row.id)));
    await load();
  };

  return {
    isLoading,
    error,
    chartAccounts,
    chartLevels,
    detailNodes,
    detailLevelsOrdered,
    hierarchyLevels,
    detailNodesByLevel,
    detailNodeById,
    hierarchyTree,
    visibleTree,
    selectedPath,
    setSelectedPath,
    treeSearch,
    setTreeSearch,
    filteredChartAccounts,
    selectedPathLabel,
    findNodeIdByPath,
    updateLevelDraft,
    saveLevels,
    saveDetailNode,
    saveChartAccount,
    removeChartAccounts,
    reload: load,
  };
}
